import { useState, useContext, useMemo, createRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { GlobalStates } from "../GlobalStates";
import { Box } from '@mui/material';
import { useLocation } from "react-router-dom";
import { useUrlParams } from "../Hooks/useUrlParams";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "../services/supabaseClient";
import { useCompanies } from "../Hooks/useCompanies";
import useGlobalData from "../Hooks/useGlobalData";
import { useTendencijosFilters } from "../Hooks/useTendencijosFilters";
import TendencijosTable from "../Components/TendencijosTable";
import FilterHeader from "../Components/Tendencijos/FilterHeader.optimized";
import useCompanyData from "../Hooks/useCompanyData";
import useBendriRezultatai from "../Hooks/useBendriRezultatai";

const Tendencijos = () => {
    const ref = createRef();
    const { t } = useTranslation();
    const location = useLocation();
    const { user, type } = useContext(GlobalStates);
    const { params: filters, clearParam } = useUrlParams();
    const queryClient = useQueryClient();
    const [drawer, setDrawer] = useState(false);

    const isAdmin = useMemo(() => type === 'Admin', [type]); // Pakeista iš type === 0

    const { originalData, companies, selectedCompany, setSelectedCompany } = useGlobalData();
    // Dvi atskiros būsenos geresnei UI patirčiai

    // Simple local filter state with global company sync
    const [filtersMain, setFilterMain] = useState(() => {
        const locationFilters = location?.state?.filter || {};

        return {
            kasPildo: locationFilters.kasPildo || 'Darbuotojo forma',
            imonesPavadinimas: selectedCompany || locationFilters.imonesPavadinimas,
            ...locationFilters
        };
    });

        // Use our specialized hooks for more efficient data loading
    const { isLoading: isCompanyDataLoading } = useCompanyData({ isAdmin, companyName: filtersMain.imonesPavadinimas, kasPildo: filtersMain.kasPildo, year: filtersMain.metai });
    const { hasBendrusRezultatus } = useBendriRezultatai();
    
    // Įmonių sąrašo gavimas su optimizuotu hook'u
    const { 
        isLoading: isLoadingCompanies
    } = useCompanies({ kasPildo: filtersMain.kasPildo });

    // Simplified sync - only update local filters when global state changes
    useEffect(() => {
        if (selectedCompany && selectedCompany !== filtersMain.imonesPavadinimas) {
            setFilterMain(prev => ({
                ...prev,
                imonesPavadinimas: selectedCompany
            }));
        }
    }, [selectedCompany, setFilterMain]);

    // Sync local to global only when setting a specific company
    useEffect(() => {
        if (filtersMain.imonesPavadinimas && filtersMain.imonesPavadinimas !== selectedCompany) {
            setSelectedCompany(filtersMain.imonesPavadinimas);
        }
    }, [filtersMain.imonesPavadinimas, selectedCompany, setSelectedCompany]);




    // Data filtering for UI
    const { tvarkyta = [], isfiltruotiTipai = [] } = useTendencijosFilters(
        originalData?.atsakymai?.length > 0 && originalData?.tipai?.length > 0 
            ? originalData 
            : { atsakymai: [], tipai: [] },
        filtersMain, 
        filters
    );
    
    // Filter loading state
    const isLoading = useMemo(() => 
        isLoadingCompanies || isCompanyDataLoading,
        [isLoadingCompanies, isCompanyDataLoading]
    );

    // Check if we have common results available for this filter
    const hasBendriRezultataiAvailable = useMemo(() => {
        return hasBendrusRezultatus({
            formosTipas: filtersMain.type || 'Darbuotojo forma',
            kasPildo: filtersMain.kasPildo || 'Darbuotojai',
            metai: filtersMain.metai ? Number(filtersMain.metai) : null
        });
    }, [hasBendrusRezultatus, filtersMain.type, filtersMain.kasPildo, filtersMain.metai]);

    // Memoize FilterHeader props to prevent unnecessary re-renders
    const memoizedFilterHeaderProps = useMemo(() => ({
        filtersMain,
        setFilterMain,
        companies,
        user,
        clearParam,
        isLoading,
        drawer,
        setDrawer,
        isAdmin
    }), [filtersMain, setFilterMain, companies, user, clearParam, isLoading, drawer, setDrawer, isAdmin]);

    // Memoize TendencijosTable props 
    const memoizedTableProps = useMemo(() => ({
        tipai: isfiltruotiTipai,
        info: tvarkyta,
        pilnaInfo: originalData.atsakymai,
        filtrai: filters,
        filtraiMain: filtersMain,
        loading: isLoading,
        hasBendriRezultatai: hasBendriRezultataiAvailable
    }), [isfiltruotiTipai, tvarkyta, originalData.atsakymai, filters, filtersMain, isLoading, hasBendriRezultataiAvailable]);

    // Prefetch data for Rezultatai page to improve navigation
    useEffect(() => {
        if (filtersMain.kasPildo && !isLoading) {
            const currentYear = new Date().getFullYear();
            
            // Prefetch companies for Rezultatai using correct query key and source
            queryClient.prefetchQuery({
                queryKey: ["companies-rezultatai", filtersMain.kasPildo],
                queryFn: async () => {
                    const { data } = await supabase
                        .from("uzpildytos_imones")
                        .select("imones_pavadinimas")
                        .contains("kas_pilde", [filtersMain.kasPildo]);
                    return data.map(({ imones_pavadinimas }) => imones_pavadinimas);
                },
                staleTime: 10 * 60 * 1000,
            });

            // Prefetch years for Rezultatai
            queryClient.prefetchQuery({
                queryKey: ["years"],
                queryFn: async () => {
                    const { data } = await supabase
                        .rpc("get_distinct_years")
                        .order("year_created", { ascending: false });
                    return data?.map(({ year_created }) => Number(year_created));
                },
                staleTime: 15 * 60 * 1000,
            });

            // Prefetch current year's data if company is selected
            if (filtersMain.imonesPavadinimas) {
                queryClient.prefetchQuery({
                    queryKey: ["answers", currentYear, filtersMain.kasPildo, filtersMain.imonesPavadinimas],
                    queryFn: async () => {
                        const query = supabase
                            .from("atsakymai")
                            .select("*")
                            .eq("yearCreated", currentYear)
                            .eq("kas_pildo", filtersMain.kasPildo)
                            .eq("imones_pavadinimas", filtersMain.imonesPavadinimas);
                        const { data } = await query;
                        return data?.map(item => ({
                            id: item.id,
                            attributes: Object.entries(item).reduce((acc, [key, value]) => {
                                if (key === "id") return acc;
                                const fieldMap = {
                                    formos_pavadinimas: "formosPavadinimas",
                                    imones_pavadinimas: "imonesPavadinimas",
                                    form_object: "formObject",
                                    kas_pildo: "kasPildo",
                                    valid_to: "validTo",
                                    created_at: "createdAt",
                                    updated_at: "updatedAt",
                                    uzpildymo_data: "uzpildymoData",
                                };
                                const newKey = fieldMap[key] || key;
                                acc[newKey] = value;
                                return acc;
                            }, {}),
                        })) || [];
                    },
                    staleTime: 3 * 60 * 1000,
                });
            }
        }
    }, [filtersMain.kasPildo, filtersMain.imonesPavadinimas, isLoading, queryClient]);

    return (
        <div className="tendencijosCont stickyParent">
            <div className="standartPadding">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <p className="rezTenName accorcionName">
                        {t('rezultataiTendencijos')}
                        {hasBendriRezultataiAvailable && (
                            <span className="bendriRezIndicator" style={{ fontSize: '0.75rem', marginLeft: '8px' }}>
                                {t('Bendri rezultatai prieinami')}
                            </span>
                        )}
                    </p>
                </div>
            </div>
            
            <FilterHeader {...memoizedFilterHeaderProps} />

            <div className="standartPadding bgBlue tendRezMargin">
                <div className="bgBlue pdfExport" ref={ref}>
                    <Box
                        mt={4}
                        display="flex" 
                        flexDirection="column" 
                        alignItems="center" 
                        justifyContent="center" 
                    >
                        <TendencijosTable {...memoizedTableProps} />
                    </Box>
                </div>
                {!isCompanyDataLoading && (tvarkyta.length === 0 || isfiltruotiTipai.length === 0) && (
                    <div className="neraAts">{t('Nera_atsakymu')}</div>
                )}
            </div>
        </div>
    );
};

export default Tendencijos;


